import { useTranslation } from "react-i18next";
import { useStore } from "@/lib/store";
import { Rocket, ShoppingBag, TrendingUp, Camera, Calendar, Flame } from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface StoryPreset {
  id: string;
  icon: LucideIcon;
  accentClass: string;
  titleKey: string;
  titleFallback: string;
  descKey: string;
  descFallback: string;
  promptKey: string;
  promptFallback: string;
  flowId: string;
  frameCount: number;
  backgroundMode: "consistent" | "varied";
}

const PRESETS: StoryPreset[] = [
  {
    id: "course-launch",
    icon: Rocket,
    accentClass: "from-[#ef3e29] to-[#d63520]",
    titleKey: "storiesCreator.presets.courseLaunch.title",
    titleFallback: "Launch kursu / mentoringu",
    descKey: "storiesCreator.presets.courseLaunch.desc",
    descFallback: 'Hook → ujawnienie autorytetu → pivot → dowód → CTA "Reply"',
    promptKey: "storiesCreator.presets.courseLaunch.prompt",
    promptFallback:
      "Stories sprzedażowe pod launch kursu [TEMAT]. Target: [opisz odbiorcę]. " +
      "Zacznij od kontrowersji w branży, pokaż mój autorytet (ile lat / ile klientów), " +
      "zrób pivot „ale teraz uchylam rąbka”, dodaj 1-2 social proof i zakończ CTA „Reply 'KURS'”.",
    flowId: "hook-truth-pivot-cta",
    frameCount: 5,
    backgroundMode: "consistent",
  },
  {
    id: "product-sale",
    icon: ShoppingBag,
    accentClass: "from-emerald-500 to-emerald-700",
    titleKey: "storiesCreator.presets.productSale.title",
    titleFallback: "Sprzedaż produktu",
    descKey: "storiesCreator.presets.productSale.desc",
    descFallback: "Problem → agitacja → rozwiązanie z produktem → CTA",
    promptKey: "storiesCreator.presets.productSale.prompt",
    promptFallback:
      "Stories sprzedażowe dla produktu [NAZWA — KATEGORIA]. Target: [opisz odbiorcę]. " +
      "Zacznij od bólu z którym mierzy się klient, eskaluj dlaczego inne rozwiązania zawodzą, " +
      "pokaż produkt jako unikalne rozwiązanie, dodaj jeden mocny dowód (liczba/case) " +
      "i zakończ CTA z konkretnym keywordem.",
    flowId: "problem-agitation-solution",
    frameCount: 5,
    backgroundMode: "consistent",
  },
  {
    id: "case-study",
    icon: TrendingUp,
    accentClass: "from-sky-500 to-sky-700",
    titleKey: "storiesCreator.presets.caseStudy.title",
    titleFallback: "Case study klienta",
    descKey: "storiesCreator.presets.caseStudy.desc",
    descFallback: "Punkt startu → problem → co zrobiliśmy → wynik (liczby) → CTA",
    promptKey: "storiesCreator.presets.caseStudy.prompt",
    promptFallback:
      "Case study klienta [BRANŻA / NAZWA]. Punkt wyjścia: [opis stanu początkowego]. " +
      "Problem: [konkretny pain]. Co zrobiliśmy: [3 kluczowe działania]. " +
      "Rezultat: [liczby / kwoty / procenty]. Zakończ CTA „Reply 'CASE' wyślę PDF / nagranie”.",
    flowId: "case-study",
    frameCount: 5,
    backgroundMode: "varied",
  },
  {
    id: "behind-the-scenes",
    icon: Camera,
    accentClass: "from-amber-500 to-amber-700",
    titleKey: "storiesCreator.presets.bts.title",
    titleFallback: "Behind the scenes",
    descKey: "storiesCreator.presets.bts.desc",
    descFallback: "Tease → kulisy procesu → insight → lekcja → CTA",
    promptKey: "storiesCreator.presets.bts.prompt",
    promptFallback:
      "Behind the scenes z [WYDARZENIE / PROCES]. Pokaż kulisy o których nikt nie mówi w branży [BRANŻA]. " +
      "Zacznij od „nikt o tym nie mówi”, pokaż 2-3 raw kadry z procesu, wyciągnij 1 mocny insight, " +
      "zakończ CTA bez nachalnej sprzedaży („Reply 'WIĘCEJ' jeśli chcesz takie kulisy regularnie”).",
    flowId: "behind-the-scenes",
    frameCount: 5,
    backgroundMode: "varied",
  },
  {
    id: "live-promo",
    icon: Calendar,
    accentClass: "from-purple-500 to-purple-700",
    titleKey: "storiesCreator.presets.livePromo.title",
    titleFallback: "Teaser premiery / live",
    descKey: "storiesCreator.presets.livePromo.desc",
    descFallback: "Data + hook → co odkryję → dlaczego to ważne → urgency → CTA",
    promptKey: "storiesCreator.presets.livePromo.prompt",
    promptFallback:
      "Teaser pod live / premierę [TYTUŁ] w dniu [DATA, GODZINA]. Target: [odbiorca]. " +
      "Zacznij od „[Data o godz. X] ujawniam…”, pokaż 2-3 rzeczy które uczestnik zabierze, " +
      "wyjaśnij dlaczego to ważne TERAZ, dodaj urgency (limit miejsc / jednorazowość), " +
      "zakończ CTA „Reply 'LIVE' wyślę szczegóły”.",
    flowId: "live-promo-teaser",
    frameCount: 5,
    backgroundMode: "consistent",
  },
  {
    id: "contrarian",
    icon: Flame,
    accentClass: "from-rose-500 to-rose-700",
    titleKey: "storiesCreator.presets.contrarian.title",
    titleFallback: "Kontrowersja / opinia",
    descKey: "storiesCreator.presets.contrarian.desc",
    descFallback: "Pattern interrupt → moja prawda → dowód → reframe → CTA",
    promptKey: "storiesCreator.presets.contrarian.prompt",
    promptFallback:
      "Stories z kontrowersyjną opinią o [TEMAT W BRANŻY]. Idź pod prąd popularnej opinii. " +
      "Hook = mocny pattern interrupt („X jest martwe”, „Wszyscy się mylą co do Y”), " +
      "pokaż moje doświadczenie (autorytet), daj dowód który łamie mit, " +
      "zrób reframe „prawdziwa droga to…”, zakończ CTA wywołującym dyskusję.",
    flowId: "hook-truth-pivot-cta",
    frameCount: 5,
    backgroundMode: "varied",
  },
];

interface Props {
  onSelectPreset: (prompt: string) => void;
}

export default function StoriesPresetGallery({ onSelectPreset }: Props) {
  const { t } = useTranslation();
  const { setStoriesFlowId, setStoriesFrameCount, setStoriesBackgroundMode } = useStore();

  const handleClick = (preset: StoryPreset) => {
    setStoriesFlowId(preset.flowId);
    setStoriesFrameCount(preset.frameCount);
    setStoriesBackgroundMode(preset.backgroundMode);
    onSelectPreset(t(preset.promptKey, preset.promptFallback));
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {PRESETS.map((preset) => {
        const Icon = preset.icon;
        return (
          <button
            key={preset.id}
            onClick={() => handleClick(preset)}
            className="group text-left bg-white/[0.03] hover:bg-white/[0.06] border border-white/[0.06] hover:border-[#ef3e29]/40 rounded-xl p-4 transition-all"
            data-testid={`preset-${preset.id}`}
          >
            <div
              className={`w-10 h-10 rounded-lg bg-gradient-to-br ${preset.accentClass} flex items-center justify-center mb-3 shadow-md`}
            >
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div className="text-white text-sm font-semibold mb-1.5 group-hover:text-[#ef3e29] transition-colors">
              {t(preset.titleKey, preset.titleFallback)}
            </div>
            <div className="text-white/45 text-[11px] leading-relaxed mb-2.5">
              {t(preset.descKey, preset.descFallback)}
            </div>
            <div className="flex flex-wrap gap-1 text-[9px] uppercase tracking-wider text-white/40">
              <span className="bg-white/[0.04] px-1.5 py-0.5 rounded">
                {preset.frameCount} {t("storiesCreator.presets.frames", "klatki")}
              </span>
              <span className="bg-white/[0.04] px-1.5 py-0.5 rounded">
                {preset.backgroundMode === "consistent"
                  ? t("storiesCreator.presets.bgConsistent", "spójne tło")
                  : t("storiesCreator.presets.bgVaried", "różne tła")}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}
